import { useState, useEffect, useRef, useCallback } from "react";
import { Popup, Toast } from "antd-mobile";

/**
 * InputAudio — 公共录音输入组件
 *
 * 基于 recorder-core H5 浏览器录音方案：
 * - 真实麦克风录音（mp3 编码）
 * - 实时波形可视化（WaveView）
 * - 录音/暂停/继续/完成 完整流程
 * - 录音后播放预览
 *
 * Props:
 *   open          是否显示弹窗
 *   onClose       关闭弹窗回调
 *   onConfirm     录音完成回调，返回 { blob: Blob, url: string, duration: number }
 *   value         已有音频的 URL（编辑回显用）
 */
// ── recorder-core 类型声明 ──
declare const Recorder: {
  Support: () => boolean;
  WaveView: (opts: { compatibleCanvas: HTMLCanvasElement; width: number; height: number }) => any;
  Open: () => Promise<boolean>;
  (opts?: any): any; // 构造函数
};

// ── 模块是否已加载 ──
let _recLoaded = false;

/** 按正确顺序加载 recorder-core 及其依赖（解决 UMD IIFE 并行加载 i18n 未定义的问题） */
async function ensureRecorderLoaded(): Promise<void> {
  if (_recLoaded) return;
  // 1. 先加载核心库（内置 i18n 初始化）
  await import("recorder-core");
  // 2. 再按序加载编码引擎和插件
  await import("recorder-core/src/engine/mp3");
  await import("recorder-core/src/engine/mp3-engine");
  await import("recorder-core/src/extensions/waveview");
  _recLoaded = true;
}

/** 获取全局 Recorder 引用（动态加载后 window.Recorder 可用） */
function getRecorder() {
  return (window as any).Recorder;
}

const BRAND = "#FF6B35";

interface InputAudioProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: (data: { blob: Blob; url: string; duration: number }) => void;
  value?: string; // 已有音频 URL
}

type RecState = "idle" | "recording" | "paused";

export default function InputAudio({ open, onClose, onConfirm, value }: InputAudioProps) {
  // ── 录音实例引用 ──
  const recRef = useRef<any>(null);
  const waveViewRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const durationTimer = useRef<ReturnType<typeof setInterval>>(null!);

  // ── 状态 ──
  const [recState, setRecState] = useState<RecState>("idle");
  const [duration, setDuration] = useState(0);      // 录音时长(秒)
  const [powerLevel, setPowerLevel] = useState(0);    // 音量等级 0-100
  const [audioUrl, setAudioUrl] = useState<string>(value || ""); // 最终音频预览 URL
  const [playing, setPlaying] = useState(false);        // 播放状态
  const audioRef = useRef<HTMLAudioElement>(null);

  // ── 初始化录音引擎（按序加载 + 权限请求）──
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        await ensureRecorderLoaded();
        const Rec = getRecorder();
        if (!Rec.Support()) {
          Toast.show({ icon: "fail", content: "当前环境不支持录音" });
          onClose?.();
          return;
        }
      } catch (err) {
        console.error("录音引擎加载失败:", err);
        Toast.show({ icon: "fail", content: "录音组件加载失败" });
      }
    })();
  }, [open]);

  // ── 打开弹窗时初始化 WaveView Canvas ──
  useEffect(() => {
    if (!open || !canvasRef.current) return;

    const cvs = canvasRef.current;
    // 设置 canvas 尺寸匹配 CSS 显示尺寸
    const rect = cvs.getBoundingClientRect();
    cvs.width = Math.max(rect.width, 300) * (window.devicePixelRatio || 1);
    cvs.height = Math.max(rect.height, 80) * (window.devicePixelRatio || 1);

    waveViewRef.current = getRecorder().WaveView({
      compatibleCanvas: cvs,
      width: cvs.width,
      height: cvs.height,
    });
  }, [open]);

  // ── 开始录音 ──
  const handleStartRecord = async () => {
    try {
      await ensureRecorderLoaded();
      const Rec = getRecorder();
      await Rec.Open();
      recRef.current = Rec({
        type: "mp3",
        sampleRate: 16000,
        bitRate: 16,
        numberOfChannels: 1,
        duration: 60000, // 最长 60 秒
      });

      recRef.current.onProcess = (
        buffers: any[],
        powerLevelVal: number,
        durationVal: number,
        _sampleRate: number,
        _newBufferIdx: number,
        asyncEnd: boolean,
      ) => {
        setDuration(Math.floor(durationVal));
        setPowerLevel(powerLevelVal);

        // 输入波形数据到 WaveView
        if (waveViewRef.current && buffers.length > 0) {
          waveViewRef.current.input(buffers[buffers.length - 1], powerLevelVal, 16000);
        }
      };

      recRef.current.start();
      setRecState("recording");

      // 启动时长计时
      if (durationTimer.current) clearInterval(durationTimer.current);
      durationTimer.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
    } catch (err) {
      console.error("启动录音失败:", err);
      Toast.show({ icon: "fail", content: "无法启动录音，请检查麦克风权限" });
    }
  };

  // ── 暂停录音 ──
  const handlePauseRecord = () => {
    if (recRef.current) {
      recRef.current.pause();
      setRecState("paused");
    }
    if (durationTimer.current) clearInterval(durationTimer.current);
  };

  // ── 继续录音 ──
  const handleResumeRecord = () => {
    if (recRef.current) {
      recRef.current.resume();
      setRecState("recording");
      durationTimer.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
    }
  };

  // ── 停止录音并获取结果 ──
  const handleStopAndUpload = useCallback(async () => {
    if (!recRef.current || recState === "idle") return;
    if (durationTimer.current) clearInterval(durationTimer.current);

    try {
      recRef.current.stop(
        (blob: Blob, _duration: number, mime: string) => {
          // 生成本地预览 URL
          const localUrl = URL.createObjectURL(blob);
          setAudioUrl(localUrl);
          setRecState("idle");

          // 清理旧录音实例
          recRef.current.close();
          recRef.current = null;

          // 回调通知父组件
          onConfirm?.({ blob, url: localUrl, duration });
        },
        (err: string) => {
          console.error("停止录音失败:", err);
          Toast.show({ icon: "fail", content: "停止录音失败" });
          setRecState("idle");
        },
      );
    } catch (e) {
      console.error(e);
    }
  }, [recState, onConfirm]);

  // ── 播放控制 ──
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  // ── 关闭弹窗时清理资源 ──
  const handleClose = () => {
    try {
      if (recRef.current) {
        recRef.current.close();
        recRef.current = null;
      }
    } catch {/* ignore */}
    if (durationTimer.current) clearInterval(durationTimer.current);
    setRecState("idle");
    setDuration(0);
    setPowerLevel(0);
    onClose?.();
  };

  // ── 格式化时长 mm:ss ──
  const fmtTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // 未打开时不渲染
  return (
    <Popup
      visible={open}
      onMaskClick={handleClose}
      position="bottom"
      bodyStyle={{
        borderRadius: "16px 16px 0 0",
        background: "#fff",
        maxHeight: "60vh",
        overflow: "hidden",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
      }}
    >
      <div className="flex flex-col" style={{ maxHeight: "60vh" }}>
        {/* 顶部拖拽条 */}
        <div className="pt-3 pb-2 flex items-center justify-center">
          <div className="w-10 h-1 rounded-full" style={{ background: "#E5E7EB" }} />
        </div>

        <div className="px-4 pb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">语音输入</h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full active:bg-gray-100 cursor-pointer"
            aria-label="关闭"
          >
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {/* 提示语 */}
          {!audioUrl ? (
            <p className="text-sm text-center text-gray-500 mb-4">
              用声音传递你的心情吧~
            </p>
          ) : (
            <p className="text-sm font-medium text-center mb-2" style={{ color: BRAND }}>
              ✓ 录制完成 ({fmtTime(duration)})
            </p>
          )}

          {/* ── 录音中界面 ── */}
          {!audioUrl ? (
            <>
              {recState !== "idle" && (
                /* 波形可视化区域 */
                <div className="mb-4 w-full h-[88px] rounded-xl overflow-hidden" style={{ background: "#F8FAFC" }}>
                  <canvas
                    ref={canvasRef}
                    type="2d"
                    className="w-full h-full block"
                    style={{ display: "block", width: "100%", height: "100%" }}
                  />
                </div>
              )}

              {/* 录音状态指示 */}
              {recState !== "idle" && (
                <div className="flex items-center justify-center gap-4 mb-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-mic-fill" style={{ color: recState === "recording" ? BRAND : "#9ca3af" }} />
                    {recState === "recording" ? "录制中..." : "已暂停"}
                  </span>
                  <span className="tabular-nums">{fmtTime(duration)}</span>
                </div>
              )}

              {/* 操作按钮区 */}
              {recState === "idle" ? (
                /* 点击开始录音 */
                <button
                  onClick={handleStartRecord}
                  className="mx-auto w-[148px] h-[148px] rounded-full flex flex-col items-center justify-center cursor-pointer active:scale-[0.95] transition-transform"
                  style={{
                    background: BRAND,
                    boxShadow: `0 10px 30px ${BRAND}35`,
                  }}
                >
                  <i className="ri-mic-line text-3xl text-white mb-1" />
                  <span className="text-base font-bold text-white">点击录制</span>
                </button>
              ) : (
                /* 录音中操作按钮组 */
                <div className="space-y-3">
                  <button
                    onClick={recState === "recording" ? handlePauseRecord : handleResumeRecord}
                    disabled={false}
                    className="w-full py-2.5 rounded-full text-sm font-medium cursor-pointer active:opacity-70 transition-opacity border"
                    style={{
                      color: recState === "recording" ? BRAND : "#666",
                      background: recState === "recording" ? `${BRAND}10` : "transparent",
                      borderColor: recState === "recording" ? `${BRAND}30` : "#eee",
                    }}
                  >
                    {recState === "recording" ? "暂停录制" : "继续录制"}
                  </button>

                  <button
                    onClick={handleStopAndUpload}
                    className="w-full py-2.5 rounded-full text-sm font-semibold cursor-pointer active:scale-[0.98] transition-all"
                    style={{ background: BRAND, color: "#fff" }}
                  >
                    完成并上传
                  </button>
                </div>
              )}
            </>
          ) : (
            /* ── 录音完成 + 预览界面 ── */
            <div className="space-y-3">
              {/* 音频信息卡片 */}
              <div className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl" style={{ border: `1px solid ${BRAND}30`, background: `${BRAND}08` }}>
                <i className="ri-mic-fill shrink-0" style={{ color: BRAND, fontSize: 18 }} />
                <span className="text-sm font-medium truncate" style={{ color: BRAND }}>语音介绍</span>
                <span className="ml-auto text-xs text-gray-400 tabular-nums">{fmtTime(duration)}</span>
              </div>

              {/* 播放控制条 */}
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer active:scale-[0.95] transition-transform"
                  style={{ background: `${BRAND}15`, color: BRAND }}
                >
                  <i className={`${playing ? "ri-pause-circle-fill" : "ri-play-fill-line"} text-base`} />
                </button>

                <span className="text-xs text-gray-400 flex-1 text-center">
                  {playing ? "正在播放..." : "点击播放预览"}
                </span>

                <button
                  onClick={() => {
                    setAudioUrl("");
                    setPlaying(false);
                    setDuration(0);
                    setPowerLevel(0);
                    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer active:opacity-70 transition-opacity"
                  style={{ color: BRAND, border: `1px solid ${BRAND}30` }}
                >
                  重新录制
                </button>
              </div>

              <audio
                ref={audioRef}
                src={audioUrl}
                preload="metadata"
                onEnded={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              >
                您的浏览器不支持音频播放。
              </audio>
            </div>
          )}
        </div>

        {/* 底部操作区 */}
        {!audioUrl && (
          <div className="px-4 pt-2">
            <button
              onClick={handleClose}
              className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-500 bg-gray-100 cursor-pointer active:bg-gray-200"
            >
              取消
            </button>
          </div>
        )}
      </div>
    </Popup>
  );
}
